import React from 'react';
import DOMPurify from 'dompurify';
import { Cross2Icon } from "@radix-ui/react-icons";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { closeJiraCard } from '../../store/chatSlice';
import logger from '../../utils/logger';
import '../Chat/JiraMarkdown.css';
import { useTheme } from '../Theme/ThemeProvider';

export function JiraCardModal() {
  const dispatch = useDispatch();
  const { mode } = useTheme();
  const jiraCardHtml = useSelector((state: RootState) => state.chat.openJiraCardHtml);
  const isOpen = !!jiraCardHtml;
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [zoomOrigin, setZoomOrigin] = React.useState<'center center' | string>('center center');
  const cardRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);
  const [translate, setTranslate] = React.useState<{x: number, y: number}>({x: 0, y: 0});
  const [transitionStyle, setTransitionStyle] = React.useState('transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)');
  
  logger.log('[JiraCardModal] Rendering:', { isOpen, htmlLength: jiraCardHtml?.length, isZoomed });

  const handleClose = React.useCallback(() => {
    dispatch(closeJiraCard());
    setIsZoomed(false);
    setTranslate({ x: 0, y: 0 });
    setZoomOrigin('center center');
    setTransitionStyle('transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)');
  }, [dispatch]);
  // const handleZoom = React.useCallback(() => {
  //   setIsZoomed(!isZoomed);
  // }, [isZoomed]);
  React.useEffect(() => {
    if (!isOpen || !cardRef.current) return;
    // Wait for card to render
    const resize = () => {
      if (!cardRef.current) return;
      const modalHeight = window.innerHeight;
      const cardHeight = cardRef.current.scrollHeight;
      // Subtract padding for modal (32px)
      const availableHeight = modalHeight - 32;
      const newScale = availableHeight / cardHeight;
      setScale(newScale < 1 ? newScale : 1);
      if (!isZoomed) setZoomOrigin('center center');
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [isOpen, jiraCardHtml, isZoomed]);

  // Handle escape key and prevent scrolling
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  // No zoom/scale logic needed

  if (!isOpen) return null;

  // Extract only the content inside <article> tags
  const articleMatch = jiraCardHtml?.match(/<article[^>]*>([\s\S]*?)<\/article>/i);
  const articleInner = articleMatch ? articleMatch[1] : jiraCardHtml;
  const articleTag = jiraCardHtml?.match(/<article([^>]*)>/i);
  const articleAttrs = articleTag ? articleTag[1] : '';
  const rawArticleHtml = `<article${articleAttrs}>${articleInner}</article>`;
  const articleHtml = DOMPurify.sanitize(rawArticleHtml, { USE_PROFILES: { html: true } });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center animate-in fade-in-0 duration-200"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={handleClose} // Close when clicking backdrop
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="fixed right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 animate-in slide-in-from-top-2 duration-300"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          padding: '8px',
          borderRadius: '6px',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px'
        }}
      >
        <Cross2Icon className="h-6 w-6" />
        <span className="sr-only">Close</span>
      </button>

      {/* Jira Card */}
      {articleHtml && (
        <div
          className="jira-card-modal-scroll-container"
          ref={scrollContainerRef}
          style={{
            width: '100vw',
            height: '100vh',
            overflow: isZoomed ? 'auto' : 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            ref={cardRef}
            onClick={(e) => {
              e.stopPropagation();
              if (!cardRef.current) return;
              const rect = cardRef.current.getBoundingClientRect();
              const modalWidth = window.innerWidth;
              const modalHeight = window.innerHeight;
              if (!isZoomed) {
                // Zoom in
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                const xPct = (x / rect.width) * 100;
                const yPct = (y / rect.height) * 100;
                setTransitionStyle('none');
                setZoomOrigin(`${xPct}% ${yPct}%`);
                const scaleFactor = 1.5;
                // Calculate offset from card top-left to click point
                const offsetX = x;
                const offsetY = y;
                // After scaling, the offset from top-left to the origin increases by (scale - 1) * offset
                const scaledOffsetX = offsetX * (scaleFactor - 1);
                const scaledOffsetY = offsetY * (scaleFactor - 1);
                // The card's top-left after scaling
                const scaledCardLeft = rect.left - scaledOffsetX;
                const scaledCardTop = rect.top - scaledOffsetY;
                // The clicked point's absolute position after scaling
                const scaledOriginX = scaledCardLeft + x * scaleFactor;
                const scaledOriginY = scaledCardTop + y * scaleFactor;
                // Center the scaled origin in the viewport
                const modalCenterX = modalWidth / 2;
                const modalCenterY = modalHeight / 2;
                const dx = modalCenterX - scaledOriginX;
                const dy = modalCenterY - scaledOriginY;
                setTranslate({x: 0, y: 0});
                setTimeout(() => {
                  setTransitionStyle('transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)');
                  setTranslate({x: dx, y: dy});
                  setIsZoomed(true);
                }, 10);
              } else {
                // Zoom out
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollTo({ left: 0, top: 0, behavior: 'auto' });
                }
                setTransitionStyle('transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)');
                setTranslate({x: 0, y: 0});
                setTimeout(() => {
                  setIsZoomed(false);
                  setTimeout(() => {
                    setTransitionStyle('none');
                    setZoomOrigin('center center');
                  }, 400);
                }, 10);
              }
            }}
            className="jira-card-content animate-in zoom-in-95 duration-300"
            data-mode={mode}
            style={{
              width: 'auto',
              maxWidth: 'unset',
              minHeight: 0,
              minWidth: 0,
              transform: isZoomed
                ? `scale(1.5) translate(${translate.x}px, ${translate.y}px)`
                : `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`,
              transformOrigin: zoomOrigin,
              transition: transitionStyle,
              borderRadius: '8px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              cursor: isZoomed ? 'zoom-out' : 'zoom-in',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'stretch',
              alignItems: 'center',
            }}
            dangerouslySetInnerHTML={{ __html: articleHtml }}
          />
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
